<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterView, useRoute, useRouter } from 'vue-router'
import AppShell from './components/shell/AppSidebar/AppShell.vue'
import InboxNotices from './components/shell/InboxNotices/InboxNotices.vue'
import AppToaster from './components/primitives/AppToaster/AppToaster.vue'
import AppAlertDialog from './components/primitives/AppAlertDialog/AppAlertDialog.vue'
import AppNotice from './components/primitives/AppNotice/AppNotice.vue'
import AppButton from './components/primitives/AppButton/AppButton.vue'
import ChatSurface from './components/chat/ChatSurface/ChatSurface.vue'
import TourHost from './components/tour/TourHost.vue'
import TourHub from './components/tour/TourHub/TourHub.vue'
import FounderLetter from './components/setup/FounderLetter/FounderLetter.vue'
import CreateContentPageStep from './components/content/CreateContentDialog/CreateContentPageStep.vue'
import PostPanelHost from './components/content/PostPanelHost/PostPanelHost.vue'
import { BODY_MOTION_CLASS } from './lib/navigation/direction'
import { navigationMotion, routedViewKey } from './router/navigation'
import { releaseAllRetained, resumeRetained } from './lib/live/retainer'
import { useActivityFavicon } from './composables/useActivityFavicon'
import { useArrivalSequence } from './composables/useArrivalSequence'
import { isChatRouteName } from './composables/useChatRoute'
import { useFounderLetter } from './composables/useFounderLetter'
import { useHandoverSequence } from './composables/useHandoverSequence'
import { useTabActivity } from './composables/useTabActivity'
import { useUnreadTitle } from './composables/useUnreadTitle'
import { useArrivalStore } from './stores/arrival'
import { useDialogStore } from './stores/dialog'
import { useThemeStore } from './stores/theme'
import { useAuthStore } from './stores/auth'
import { useBrandStore } from './stores/brand'
import { useChatStore } from './stores/chat'
import { useCompetitionStore } from './stores/competition'
import { useContentStore } from './stores/content'
import { useInboxStore } from './stores/inbox'
import { usePostRunStore } from './stores/post-run'
import { runActivity } from './types/setup-run'
import { useArticleRunStore } from './stores/article-run'
import { useCreateContentStore } from './stores/create-content'
import { usePlanStore } from './stores/plan'
import { usePageStatus } from './composables/usePageStatus'
import { pagesOfTopic, topicsInOrder } from './lib/strategy/plan'
import { useLocaleStore } from './stores/locale'
import { useShellBannerStore } from './stores/shell-banner'
import { useSetupStore } from './stores/setup'
import { useTourStore } from './stores/tour'
import type { NavBrand, NavUser } from './components/shell/AppSidebar/nav-types'
import { isLoaded } from './types/load-state'

declare module 'vue-router' {
  interface RouteMeta {
    layout?: 'shell' | 'bare'
  }
}

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const isBare = computed(() => route.meta.layout === 'bare')
const isChatRoute = computed(() => isChatRouteName(route.name))
const isSetupRoute = computed(() => route.name === 'setup')
const isDesignRoute = computed(() => route.name === 'design')
const isSection = computed(() => route.matched.length > 1)

watch(
  isBare,
  (bare) => {
    document.documentElement.dataset.layout = bare ? 'bare' : 'shell'
  },
  { immediate: true },
)

const dialog = useDialogStore()
const themeStore = useThemeStore()

const arrival = useArrivalStore()
const arrivalSequence = useArrivalSequence()
const handoverSequence = useHandoverSequence()
const founderLetter = useFounderLetter()

/** Lets a bare screen blur away over the arriving shell; any other layout swap is instant. */
function leaveLayout(element: Element, done: () => void): void {
  if (isBare.value || arrival.scene !== 'arrival' || !(element instanceof HTMLElement)) {
    done()
    return
  }
  void arrivalSequence.leave(element).then(done)
}

/** Lets the setup screen blur away as setup hands over to Home; any other view change is instant. */
function leaveView(element: Element, done: () => void): void {
  if (arrival.scene !== 'handover' || !(element instanceof HTMLElement)) {
    done()
    return
  }
  void handoverSequence.leaveScreen(element).then(done)
}

const auth = useAuthStore()
const shellUser = computed<NavUser | null>(() => {
  const user = auth.user
  if (!user) return null
  return { name: user.displayName ?? user.email ?? t('nav.account'), email: user.email ?? '' }
})

const brand = useBrandStore()
const shellBrand = computed<NavBrand | null>(() => {
  const visual = isLoaded(brand.visualStyle) ? brand.visualStyle.value : null
  return {
    name: brand.brandName,
    monogramSrc: visual?.monogram?.src,
    logoSrc: visual?.logo?.src,
  }
})
const chat = useChatStore()
const content = useContentStore()
const competition = useCompetitionStore()
const inbox = useInboxStore()

useUnreadTitle(
  () => inbox.unreadCount,
  () => t('nav.inbox'),
  () => (route.meta.title ? `${t(route.meta.title)} · Raldo` : 'Raldo'),
)

const navBadges = computed(() => ({
  inbox: {
    count: inbox.unreadCount,
    tone: inbox.importantCount > 0 ? ('orange' as const) : undefined,
  },
}))
const tour = useTourStore()
const setup = useSetupStore()
const createContent = useCreateContentStore()
const plan = usePlanStore()
const articleRun = useArticleRunStore()
const postRun = usePostRunStore()
const pageStatus = usePageStatus()

useActivityFavicon(
  useTabActivity(() => ({
    chat: chat.activity,
    content: runActivity(postRun.run),
    setup: setup.activity,
  })),
)

const creating = ref(false)

const topics = computed(() => (isLoaded(plan.topics) ? topicsInOrder(plan.topics.value) : []))
const pagesToWrite = computed(() => {
  const pages = plan.pages
  if (!isLoaded(pages)) return []
  return topics.value.flatMap((topic) =>
    pagesOfTopic(pages.value, topic.id).filter((page) => pageStatus.statusOf(page) === 'planned'),
  )
})

async function onWritePage(pageId: string): Promise<void> {
  creating.value = true
  const started = await articleRun.write(pageId)
  creating.value = false
  if (!started) return
  createContent.hide()
  void router.push({ name: 'page', params: { id: pageId } })
}

/** The shell's own holds on the feeds it needs on every page; held while a brand is in scope. */
let shellHolds: Array<() => void> | null = null

watch(
  () => ({
    signedIn: auth.isSignedIn,
    workspaceId: auth.claims?.workspaceId,
    brandId: auth.claims?.brandId,
    uid: auth.user?.uid,
  }),
  (scope) => {
    releaseAllRetained()
    if (scope.signedIn && scope.workspaceId && scope.brandId) {
      shellHolds ??= [
        brand.retain(),
        setup.retain(),
        content.retain(),
        plan.retainPlan(),
        inbox.retain(),
        competition.retain(),
        tour.retain(),
      ]
      resumeRetained()
    } else {
      for (const release of shellHolds ?? []) release()
      shellHolds = null
    }
  },
  { immediate: true },
)

const localeStore = useLocaleStore()
watch(
  () => brand.contentLanguage,
  (language) => {
    if (language) localeStore.setLocale(language)
  },
  { immediate: true },
)

const shellBanner = useShellBannerStore()
const banner = computed(() => shellBanner.banner)
watch(
  () => isSetupRoute.value || route.name === 'settings-connections',
  (suppressed) => shellBanner.setSuppressed(suppressed),
  { immediate: true },
)

const showTourHub = computed(() => setup.resolved && auth.hasWorkspace && !setup.needsSetup)

async function handleSignOut(): Promise<void> {
  try {
    await auth.signOut()
  } catch {
    // auth.signOut records its own error.
  }
  void router.push('/auth')
}
</script>

<template>
  <Transition leave-active-class="z-40" @leave="leaveLayout">
    <RouterView v-if="isBare" v-slot="{ Component }">
      <component
        v-if="Component"
        :is="Component"
        :key="route.path"
        class="motion-safe:animate-view-in"
      />
    </RouterView>
    <AppShell
      v-else
      :user="shellUser"
      :brand="shellBrand"
      :dock-button="!isChatRoute && !isSetupRoute && !isDesignRoute"
      :nav-badges="navBadges"
      :setup="isSetupRoute"
      :setup-steps="setup.stepStates"
      :arrival="arrival.scene === 'arrival'"
      :handover="arrival.scene === 'handover'"
      :theme="themeStore.resolvedTheme"
      @update:theme="themeStore.setTheme"
      @arrived="arrival.disarm()"
      @handed-over="arrival.disarm()"
      @sign-out="handleSignOut"
    >
      <template v-if="showTourHub" #nav-footer>
        <TourHub />
      </template>

      <template v-if="banner" #banner>
        <AppNotice
          shape="banner"
          :tone="banner.tone"
          :message="banner.message()"
          dismissible
          @dismiss="shellBanner.dismiss()"
        >
          <template #actions>
            <AppButton
              size="xs"
              variant="secondary"
              :label="banner.actionLabel()"
              @click="banner.action()"
            />
          </template>
        </AppNotice>
      </template>

      <Teleport v-if="!isSetupRoute" defer to="#chat-dock-host" :disabled="isChatRoute">
        <ChatSurface :variant="isChatRoute ? 'full' : 'docked'" />
      </Teleport>
      <PostPanelHost v-if="!isSetupRoute && !isChatRoute" />
      <Transition :css="false" @leave="leaveView">
        <RouterView v-if="!isChatRoute" v-slot="{ Component }">
          <component
            v-if="Component"
            :is="Component"
            :key="routedViewKey(route)"
            :class="isSection ? undefined : BODY_MOTION_CLASS[navigationMotion]"
          />
        </RouterView>
      </Transition>

      <CreateContentPageStep
        v-model:open="createContent.pageStepOpen"
        :pages="pagesToWrite"
        :topics="topics"
        :submitting="creating"
        @confirm="onWritePage"
      />

      <TourHost />

      <FounderLetter
        v-if="isSetupRoute"
        :open="founderLetter.open.value"
        :first-name="founderLetter.firstName.value"
        @start="founderLetter.dismiss()"
      />

      <template v-if="!isSetupRoute" #dock>
        <div id="chat-dock-host" class="h-full" />
      </template>
    </AppShell>
  </Transition>

  <InboxNotices v-if="!isBare" />
  <AppToaster />
  <AppAlertDialog
    v-model:open="dialog.open"
    :title="dialog.title"
    :description="dialog.description"
    :confirm-label="dialog.confirmLabel"
    :cancel-label="dialog.cancelLabel"
    :variant="dialog.variant"
    @confirm="dialog.onConfirm"
    @cancel="dialog.onCancel"
  />
</template>
